/**
 * CSS özelliklerini string'e dönüştürür
 * @param {Object} styles CSS özellikleri
 * @returns {string} CSS string
 */
export const getStyles = (styles = {}) => {
    if (!styles || Object.keys(styles).length === 0) return '';
    
    return Object.entries(styles)
        .map(([key, value]) => {
            // camelCase'i kebab-case'e çevir
            const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            return `${cssKey}: ${value}`;
        })
        .join('; ');
};

/**
 * Typography özelliklerini CSS'e dönüştürür
 * @param {Object} typography Typography özellikleri
 * @returns {Object} CSS özellikleri
 */
export const getTypographyStyles = (typography = {}, important = false) => {
    const styles = {};
    
    if (typography.fontFamily) styles.fontFamily = typography.fontFamily + (important ? ' !important' : '');
    if (typography.fontSize) styles.fontSize = typography.fontSize + (important ? ' !important' : '');
    if (typography.fontWeight) styles.fontWeight = typography.fontWeight + (important ? ' !important' : '');
    if (typography.lineHeight) styles.lineHeight = typography.lineHeight + (important ? ' !important' : '');
    if (typography.letterSpacing) styles.letterSpacing = typography.letterSpacing + (important ? ' !important' : '');
    if (typography.textTransform) styles.textTransform = typography.textTransform + (important ? ' !important' : '');
    if (typography.fontStyle) styles.fontStyle = typography.fontStyle + (important ? ' !important' : '');
    if (typography.textDecoration) styles.textDecoration = typography.textDecoration + (important ? ' !important' : '');
    if (typography.color) styles.color = typography.color + (important ? ' !important' : '');
    if (typography.textAlign) styles.textAlign = typography.textAlign + (important ? ' !important' : '');
    
    return styles;
};

/**
 * Spacing özelliklerini CSS'e dönüştürür
 * @param {Object} spacing Spacing özellikleri
 * @returns {Object} CSS özellikleri
 */
export const getSpacingStyles = (spacing, prefix = '') => {
    if (!spacing) return {};

    const styles = {};

    // Desktop spacing
    if (spacing.desktop) {
        if (spacing.desktop.top) styles['top'] = `${spacing.desktop.top}px`;
        if (spacing.desktop.right) styles['right'] = `${spacing.desktop.right}px`;
        if (spacing.desktop.bottom) styles['bottom'] = `${spacing.desktop.bottom}px`;
        if (spacing.desktop.left) styles['left'] = `${spacing.desktop.left}px`;
    }

    // Tablet spacing
    if (spacing.tablet) {
        if (spacing.tablet.top) styles['top-tablet'] = `${spacing.tablet.top}px`;
        if (spacing.tablet.right) styles['right-tablet'] = `${spacing.tablet.right}px`;
        if (spacing.tablet.bottom) styles['bottom-tablet'] = `${spacing.tablet.bottom}px`;
        if (spacing.tablet.left) styles['left-tablet'] = `${spacing.tablet.left}px`;
    }

    // Mobile spacing
    if (spacing.mobile) {
        if (spacing.mobile.top) styles['top-mobile'] = `${spacing.mobile.top}px`;
        if (spacing.mobile.right) styles['right-mobile'] = `${spacing.mobile.right}px`;
        if (spacing.mobile.bottom) styles['bottom-mobile'] = `${spacing.mobile.bottom}px`;
        if (spacing.mobile.left) styles['left-mobile'] = `${spacing.mobile.left}px`;
    }

    if (prefix) {
        Object.keys(styles).forEach(key => {
            styles[`--${prefix}-${key}`] = styles[key];
            delete styles[key];
        });
    }

    return styles;
};

/**
 * Border özelliklerini CSS'e dönüştürür
 * @param {Object} border Border özellikleri
 * @returns {Object} CSS özellikleri
 */
export const getBorderStyles = (border, prefix = '') => {
    if (!border) return {};

    const styles = {};

    // Desktop border
    if (border.desktop) {
        // Border style
        if (border.desktop.style) {
            styles['border-style'] = border.desktop.style;
        }
        
        // Border color
        if (border.desktop.color) {
            styles['border-color'] = border.desktop.color;
        }
        
        // Border width
        if (border.desktop.width) {
            if (border.desktop.width.top) styles['border-top-width'] = `${border.desktop.width.top}px`;
            if (border.desktop.width.right) styles['border-right-width'] = `${border.desktop.width.right}px`;
            if (border.desktop.width.bottom) styles['border-bottom-width'] = `${border.desktop.width.bottom}px`;
            if (border.desktop.width.left) styles['border-left-width'] = `${border.desktop.width.left}px`;
        }
        
        // Border radius
        if (border.desktop.radius) {
            if (border.desktop.radius.topLeft) styles['border-top-left-radius'] = `${border.desktop.radius.topLeft}px`;
            if (border.desktop.radius.topRight) styles['border-top-right-radius'] = `${border.desktop.radius.topRight}px`;
            if (border.desktop.radius.bottomRight) styles['border-bottom-right-radius'] = `${border.desktop.radius.bottomRight}px`;
            if (border.desktop.radius.bottomLeft) styles['border-bottom-left-radius'] = `${border.desktop.radius.bottomLeft}px`;
        }
    }

    // Tablet border
    if (border.tablet) {
        // Border style
        if (border.tablet.style) {
            styles['border-style-tablet'] = border.tablet.style;
        }
        
        // Border color
        if (border.tablet.color) {
            styles['border-color-tablet'] = border.tablet.color;
        }
        
        // Border width
        if (border.tablet.width) {
            if (border.tablet.width.top) styles['border-top-width-tablet'] = `${border.tablet.width.top}px`;
            if (border.tablet.width.right) styles['border-right-width-tablet'] = `${border.tablet.width.right}px`;
            if (border.tablet.width.bottom) styles['border-bottom-width-tablet'] = `${border.tablet.width.bottom}px`;
            if (border.tablet.width.left) styles['border-left-width-tablet'] = `${border.tablet.width.left}px`;
        }
        
        // Border radius
        if (border.tablet.radius) {
            if (border.tablet.radius.topLeft) styles['border-top-left-radius-tablet'] = `${border.tablet.radius.topLeft}px`;
            if (border.tablet.radius.topRight) styles['border-top-right-radius-tablet'] = `${border.tablet.radius.topRight}px`;
            if (border.tablet.radius.bottomRight) styles['border-bottom-right-radius-tablet'] = `${border.tablet.radius.bottomRight}px`;
            if (border.tablet.radius.bottomLeft) styles['border-bottom-left-radius-tablet'] = `${border.tablet.radius.bottomLeft}px`;
        }
    }

    // Mobile border
    if (border.mobile) {
        // Border style
        if (border.mobile.style) {
            styles['border-style-mobile'] = border.mobile.style;
        }
        
        // Border color
        if (border.mobile.color) {
            styles['border-color-mobile'] = border.mobile.color;
        }
        
        // Border width
        if (border.mobile.width) {
            if (border.mobile.width.top) styles['border-top-width-mobile'] = `${border.mobile.width.top}px`;
            if (border.mobile.width.right) styles['border-right-width-mobile'] = `${border.mobile.width.right}px`;
            if (border.mobile.width.bottom) styles['border-bottom-width-mobile'] = `${border.mobile.width.bottom}px`;
            if (border.mobile.width.left) styles['border-left-width-mobile'] = `${border.mobile.width.left}px`;
        }
        
        // Border radius
        if (border.mobile.radius) {
            if (border.mobile.radius.topLeft) styles['border-top-left-radius-mobile'] = `${border.mobile.radius.topLeft}px`;
            if (border.mobile.radius.topRight) styles['border-top-right-radius-mobile'] = `${border.mobile.radius.topRight}px`;
            if (border.mobile.radius.bottomRight) styles['border-bottom-right-radius-mobile'] = `${border.mobile.radius.bottomRight}px`;
            if (border.mobile.radius.bottomLeft) styles['border-bottom-left-radius-mobile'] = `${border.mobile.radius.bottomLeft}px`;
        }
    }

    if (prefix) {
        Object.keys(styles).forEach(key => {
            // Add double 'border-' prefix for border properties

                styles[`--${key}`] = styles[key];
        
            delete styles[key];
        });
    }

    return styles;
};
